import { useState } from 'react';
import { downloadLagerMaterialsExport } from 'core/api/apiService';
import { useLanguage } from '../../../core/i18n/LanguageContext';

/**
 * useLagerExport — kapselt Export-State und -Handler fuer LagerView.
 *
 * @param {Object} user              - Auth-User (aus useAuth)
 * @param {Object} filterState       - Aktueller Filterzustand; erwartet:
 *   selectedWerk, debouncedSearch, selectedWg, selectedMa, bestandsstatus,
 *   advFilters, debouncedBestandMin, debouncedBestandMax,
 *   kritischFilter, debouncedMerkmale, sort
 * @param {Function} showError       - Fehler-Toast-Callback
 * @returns {{ exportLoading: boolean, handleExport: Function }}
 */
const useLagerExport = (user, filterState, showError) => {
  const { t } = useLanguage();
  const [exportLoading, setExportLoading] = useState(false);

  const handleExport = async () => {
    const {
      selectedWerk,
      debouncedSearch,
      selectedWg,
      selectedMa,
      bestandsstatus,
      advFilters,
      debouncedBestandMin,
      debouncedBestandMax,
      kritischFilter,
      debouncedMerkmale,
      sort,
    } = filterState;

    if (exportLoading || !selectedWerk) return;
    setExportLoading(true);
    try {
      const params = {
        werk: selectedWerk,
        search: debouncedSearch || undefined,
        warengruppe: selectedWg.length > 0 ? selectedWg.join(',') : undefined,
        materialart: selectedMa.length > 0 ? selectedMa.join(',') : undefined,
        bestandsstatus: bestandsstatus !== 'alle' ? bestandsstatus : undefined,
        lagerort: Array.isArray(advFilters.lagerort) && advFilters.lagerort.length > 0 ? advFilters.lagerort.join(',') : undefined,
        beschaffungsart: Array.isArray(advFilters.beschaffungsart) && advFilters.beschaffungsart.length > 0 ? advFilters.beschaffungsart.join(',') : undefined,
        dispomerkmal: Array.isArray(advFilters.dispomerkmal) && advFilters.dispomerkmal.length > 0 ? advFilters.dispomerkmal.join(',') : undefined,
        bestand_min: debouncedBestandMin !== '' ? debouncedBestandMin : undefined,
        bestand_max: debouncedBestandMax !== '' ? debouncedBestandMax : undefined,
        offene_banf: advFilters.offene_banf !== 'alle' ? advFilters.offene_banf : undefined,
        offene_bestellung: advFilters.offene_bestellung !== 'alle' ? advFilters.offene_bestellung : undefined,
        handlungsbedarf: advFilters.handlungsbedarf === true ? true : undefined,
        stock_type: Array.isArray(advFilters.stock_type) && advFilters.stock_type.length > 0 ? advFilters.stock_type.join(',') : undefined,
        vendor: Array.isArray(advFilters.vendor) && advFilters.vendor.length > 0 ? advFilters.vendor.join(',') : undefined,
        ekgrp: Array.isArray(advFilters.ekgrp) && advFilters.ekgrp.length > 0 ? advFilters.ekgrp.join(',') : undefined,
        sperrbestand: advFilters.sperrbestand === true ? true : undefined,
        qmbestand: advFilters.qmbestand === true ? true : undefined,
        kritisch: kritischFilter === true ? true : undefined,
        merkmale: Array.isArray(debouncedMerkmale) && debouncedMerkmale.length > 0 ? debouncedMerkmale : undefined,
        sort,
      };
      await downloadLagerMaterialsExport(user, params);
    } catch (err) {
      showError(t('lm.exportError') + ': ' + (err?.message || String(err)));
    } finally {
      setExportLoading(false);
    }
  };

  return { exportLoading, handleExport };
};

export default useLagerExport;
